import { SxProps, Theme, UseAutocompleteProps } from "@mui/material";
import Autocomplete, {
    AutocompleteProps,
    createFilterOptions,
} from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Fragment, useCallback, useMemo } from "react";

import { isDefined } from "@callveriq/utils/helper/common.helper";
import VirtualListOptionsForSelect, {
    VirtualListOptionsForSelectPaginated,
} from "./VirtualListOptionsForSelect";

export interface CustomAutoCompleteProps<
    T extends { id: number | string | null; label: string }
> {
    autocompleteProps: Omit<
        UseAutocompleteProps<
            T["id"],
            false,
            boolean | undefined,
            boolean | undefined
        >,
        "options"
    > &
        Partial<
            Omit<
                AutocompleteProps<
                    T["id"],
                    false,
                    boolean | undefined,
                    boolean | undefined
                >,
                "options"
            >
        > & {
            options: T[];
        };
    label: string;
    loading?: boolean;
    value?: T["id"] | null;
    setValue?: (val: T["id"] | null) => void;
    className?: string;
    limitTags?: number;
    sx?: SxProps<Theme>;
    error?: boolean;
    helperText?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
}

/**
 * When `isPaginated` is set the options are a server-side page: the typed text
 * is forwarded through `setQuery` (wrap the select in `PaginatedSelectProvider`
 * for scroll-to-load) and no client-side filtering is applied.
 */
export type GenericSingleSelectProps<
    T extends { id: number | string | null; label: string }
> = CustomAutoCompleteProps<T> &
    (
        | { isPaginated?: false; setQuery?: never }
        | { isPaginated: true; setQuery: (val: string) => void }
    );

const GenericSingleSelect = <
    T extends { id: number | string | null; label: string }
>(
    props: GenericSingleSelectProps<T>
) => {
    const {
        isPaginated,
        setQuery,
        label,
        value = null,
        setValue = () => {
            return;
        },
        error = false,
        helperText = "",
        placeholder = "Search",
        autocompleteProps: { options, ...rest },
        required,
        disabled = false,
        ...other
    } = props;

    const handleOptionChange: CustomAutoCompleteProps<T>["autocompleteProps"]["onChange"] =
        (e, value) => {
            if (isDefined(value)) {
                return setValue(value);
            }
            setValue(null);
        };

    const optionsObj = useMemo(() => {
        return options.reduce((acc, item) => {
            acc[item.id ?? ""] = item;
            return acc;
        }, {} as Record<string | number, T>);
    }, [options]);

    const optionsIds = useMemo(() => {
        return options.map((e) => e.id);
    }, [options]);

    const getLabel = useCallback(
        (id: T["id"]) => optionsObj[id ?? ""]?.label || "",
        [options]
    );

    return (
        <Autocomplete
            disableClearable
            multiple={false}
            options={[...optionsIds, ...(options.length ? [""] : [])]}
            getOptionLabel={(option) => getLabel(option) || ""}
            filterOptions={(opts, params) => {
                if (isPaginated) {
                    // the server already filtered this page - do not re-filter it
                    setQuery?.(params.inputValue);
                    return opts;
                }
                return createFilterOptions<T["id"]>()(opts, params);
            }}
            renderOption={(props, option) => {
                return isDefined(option) && option !== "" ? (
                    <li
                        {...props}
                        key={option}
                        id={option?.toString()}
                        className={`${props.className} !py-3`}
                        title={getLabel(option)}
                        {...(rest["aria-label"] && {
                            "aria-label": rest["aria-label"],
                        })}
                    >
                        {getLabel(option)}
                    </li>
                ) : (
                    <Fragment key={-1} />
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={<>{helperText}</>}
                    required={required}
                />
            )}
            value={value}
            onChange={handleOptionChange}
            isOptionEqualToValue={(option, value) => {
                return option === value;
            }}
            disabled={disabled}
            slotProps={{
                listbox: {
                    component: isPaginated
                        ? VirtualListOptionsForSelectPaginated
                        : VirtualListOptionsForSelect,
                },
                popper: {
                    sx: {
                        zIndex: (theme) => theme.zIndex.modal + 10,
                    },
                },
            }}
            {...rest}
            {...other}
        />
    );
};

export default GenericSingleSelect;
